// @givmo/org-console — the Branding tab's draft (EMC-WEB11; mockup 12a): what the form holds between
// saves, whether it differs from what the server holds, and the PUT it becomes. Pure.
//
// The colours, fonts and words save TOGETHER through the one Save on the save line, because the
// server replaces them whole (`PUT …/profile/branding`). The logos never ride it: each uploads the
// moment it is chosen (`POST …/branding/logos/{slot}`), so a logo is never "unsaved".

import type {
  BrandColor,
  BrandColorRole,
  BrandFontWeight,
  EmailSafeFont,
  OrgBrand,
  OrgBrandPut,
} from './org-brand-wire';

export interface BrandFontDraft {
  family: string;
  weight: BrandFontWeight;
}

export interface BrandDraft {
  colors: BrandColor[];
  headings: BrandFontDraft | null;
  body: BrandFontDraft | null;
  fallback: string;
  tagline: string;
  voiceNotes: string;
}

/** The font a brand that names none falls back to (the server's default, mockup 12a). */
export const BRAND_FALLBACK_FONT = 'arial';

export function brandDraftFrom(brand: OrgBrand): BrandDraft {
  return {
    colors: brand.colors.map((color) => ({
      ...color,
      roles: [...color.roles],
      otherRoles: [...color.otherRoles],
    })),
    headings: brand.fonts
      ? { family: brand.fonts.headings.family, weight: brand.fonts.headings.weight }
      : null,
    body: brand.fonts ? { family: brand.fonts.body.family, weight: brand.fonts.body.weight } : null,
    fallback: brand.fonts?.fallback ?? BRAND_FALLBACK_FONT,
    tagline: brand.tagline ?? '',
    voiceNotes: brand.voiceNotes ?? '',
  };
}

function blankIsNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * The PUT. Fonts are both chosen or neither (the server takes the pair or `null`); the form fills the
 * other with the fallback the moment one is chosen, so a half pair never reaches here from the UI.
 */
export function brandPutFrom(draft: BrandDraft): OrgBrandPut {
  return {
    colors: draft.colors.map((color) => ({
      name: color.name.trim(),
      hex: color.hex.toLowerCase(),
      roles: [...color.roles, ...color.otherRoles],
    })),
    fonts:
      draft.headings !== null && draft.body !== null
        ? { headings: draft.headings, body: draft.body, fallback: draft.fallback }
        : null,
    tagline: blankIsNull(draft.tagline.replace(/\s+/g, ' ')),
    voice_notes: blankIsNull(draft.voiceNotes),
  };
}

/** Whether the form holds a change the server does not — what lights Save. */
export function brandDraftChanged(draft: BrandDraft, saved: BrandDraft): boolean {
  return JSON.stringify(brandPutFrom(draft)) !== JSON.stringify(brandPutFrom(saved));
}

/**
 * A colour takes a role, or gives it up. A role belongs to at most one colour, so taking it moves it
 * off whichever colour held it (the server refuses a role on two).
 */
export function withColorRole(
  colors: readonly BrandColor[],
  index: number,
  role: BrandColorRole,
  on: boolean,
): BrandColor[] {
  return colors.map((color, i) => {
    if (i === index) {
      const roles = color.roles.filter((r) => r !== role);
      return { ...color, roles: on ? [...roles, role] : roles };
    }
    return on ? { ...color, roles: color.roles.filter((r) => r !== role) } : color;
  });
}

/** Each role in the words of the colour's usage line (mockup 12a: "Headings, buttons, QR codes"). */
export const BRAND_ROLE_WORDS: Record<BrandColorRole, string> = {
  headings: 'headings',
  buttons: 'buttons',
  links: 'links',
  accents: 'top bar, highlights',
  text: 'body text',
  qr: 'QR codes',
};

/** Each role as a choice in the colour dialog. */
export const BRAND_ROLE_LABELS: Record<BrandColorRole, string> = {
  headings: 'Headings',
  buttons: 'Buttons',
  links: 'Links',
  accents: 'Top bar and highlights',
  text: 'Body text',
  qr: 'QR codes',
};

/** The usage line under a colour: its roles in the fixed order, the first word capitalized. */
export function colorUsageLine(roles: readonly BrandColorRole[], order: readonly BrandColorRole[]) {
  const words = order.filter((role) => roles.includes(role)).map((role) => BRAND_ROLE_WORDS[role]);
  if (words.length === 0) return 'Not used for anything';
  const line = words.join(', ');
  return line.charAt(0).toUpperCase() + line.slice(1);
}

/** A font choice's value in the font selects: the font and its weight. */
export function fontOptionValue(font: BrandFontDraft): string {
  return `${font.family}:${font.weight}`;
}

export function fontFromOptionValue(value: string): BrandFontDraft | null {
  const at = value.lastIndexOf(':');
  if (at <= 0) return null;
  const weight = value.slice(at + 1);
  if (weight !== 'regular' && weight !== 'bold') return null;
  return { family: value.slice(0, at), weight };
}

/** Every email-safe font, regular and bold ("Helvetica Neue", "Helvetica Neue, bold"). */
export function fontOptions(choices: readonly EmailSafeFont[]) {
  return choices.flatMap((font) => [
    { value: fontOptionValue({ family: font.key, weight: 'regular' }), label: font.label },
    { value: fontOptionValue({ family: font.key, weight: 'bold' }), label: `${font.label}, bold` },
  ]);
}

/** The CSS a sample line is drawn in, for a draft the server has not written a stack for yet. */
export function fontSampleFamily(family: string, choices: readonly EmailSafeFont[]): string {
  const font = choices.find((choice) => choice.key === family);
  return `'${font?.label ?? 'Arial'}', Arial, ${font?.generic ?? 'sans-serif'}`;
}

/** `#rrggbb` from what a person typed (with or without `#`, three or six digits), or null. */
export function normalizeHex(value: string): string | null {
  const raw = value.trim().replace(/^#/, '').toLowerCase();
  if (/^[0-9a-f]{6}$/.test(raw)) return `#${raw}`;
  if (/^[0-9a-f]{3}$/.test(raw)) {
    return `#${raw
      .split('')
      .map((c) => c + c)
      .join('')}`;
  }
  return null;
}
